import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtD } from "@/lib/platform";

const RoleSelect = ({ roles, value, onChange, disabled, testId }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-8 text-xs w-[190px]" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
  </Select>
);

const NewUserDialog = ({ open, onOpenChange, roles, onCreated }) => {
  const [f, setF] = useState({ username: "", full_name: "", email: "", password: "", grc_role: "business_user" });
  useEffect(() => { if (open) setF({ username: "", full_name: "", email: "", password: "", grc_role: "business_user" }); }, [open]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const submit = async () => {
    try {
      await api.post("/platform/users", { ...f, email: f.email || null });
      toast.success("User created");
      onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Could not create user"));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle>Invite a user</DialogTitle>
          <DialogDescription>The user joins this organization only and cannot see other tenants' data.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {[["username", "Username / email"], ["full_name", "Full name"], ["email", "Notification email"]].map(([k, l]) => (
            <div key={k} className="space-y-1"><Label>{l}</Label><Input value={f[k]} onChange={set(k)} data-testid={`new-user-${k}`} /></div>
          ))}
          <div className="space-y-1"><Label>Temporary password (min 8)</Label>
            <Input type="password" value={f.password} onChange={set("password")} data-testid="new-user-password" /></div>
          <div className="space-y-1"><Label>Role</Label>
            <RoleSelect roles={roles} value={f.grc_role} onChange={set("grc_role")} testId="new-user-role" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!f.username || f.password.length < 8} className="bg-[#003366] hover:bg-[#003366]/90"
                  data-testid="new-user-submit">Create user</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const UsersPanel = () => {
  const auth = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const canEdit = auth.can("users", "edit");
  const isPA = auth.user?.grc_role === "platform_admin";

  const load = useCallback(() => api.get("/platform/users").then(({ data }) => setUsers(data.users)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/platform/meta").then(({ data }) => setRoles(data.roles)).catch(() => {});
  }, [load, auth.user?.tenant_id]);

  const assignable = roles.filter((r) => isPA || r.key !== "platform_admin");

  const update = async (u, patch) => {
    try {
      await api.put(`/platform/users/${u.id}`, patch);
      toast.success(`${u.username} updated`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    }
  };

  return (
    <div className="space-y-3" data-testid="users-panel">
      <div className="flex justify-end">
        {auth.can("users", "create") && (
          <Button onClick={() => setOpen(true)} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="users-invite-button">
            <UserPlus className="w-4 h-4 mr-1" /> Invite user
          </Button>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Active</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const self = u.username === auth.user?.username;
              const locked = !canEdit || self || (u.grc_role === "platform_admin" && !isPA);
              return (
                <tr key={u.id} className="border-b border-slate-50" data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#003366]">{u.full_name || u.username}{self && <span className="ml-1 text-[10px] text-slate-400">(you)</span>}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{u.username}</div>
                  </td>
                  <td className="px-4 py-3">
                    {locked ? <span className="text-xs font-semibold text-[#008080]" data-testid={`user-role-${u.username}`}>{u.role_label}</span> : (
                      <RoleSelect roles={u.grc_role === "platform_admin" ? roles : assignable} value={u.grc_role}
                                  onChange={(v) => update(u, { grc_role: v })} testId={`user-role-select-${u.username}`} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtD(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <Switch checked={u.active !== false} disabled={locked} onCheckedChange={(v) => update(u, { active: v })}
                            data-testid={`user-active-${u.username}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <NewUserDialog open={open} onOpenChange={setOpen} roles={assignable} onCreated={load} />
    </div>
  );
};
